import {
  AgeGroup,
  AttendanceFrequencyResponse,
  FrequencyTypeEnum,
  PeriodTypeEnum,
  RegisterSessionEnum,
  ResponsibleTypeEnum,
} from '@schooly/api';
import { timeToMinutes } from './time-utils';

export interface RegisterablePeriod {
  id: string;
  start_time: string;
  register_session?: RegisterSessionEnum | null;
}

/**
 * Reassigns AM/PM by time across all register-flagged periods: earliest
 * start_time -> 'am', next -> 'pm', any beyond the first two are cleared.
 * A period is "flagged" when register_session != null (value may be stale).
 */
export const deriveRegisterSessions = <T extends RegisterablePeriod>(periods: T[]): T[] => {
  const orderedTagged = periods
    .filter((p) => p.register_session != null)
    .sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));

  const sessionById = new Map<string, RegisterSessionEnum | null>();
  orderedTagged.forEach((p, index) => {
    sessionById.set(
      p.id,
      index === 0 ? RegisterSessionEnum.Am : index === 1 ? RegisterSessionEnum.Pm : null,
    );
  });

  return periods.map((p) =>
    p.register_session != null ? { ...p, register_session: sessionById.get(p.id) ?? null } : p,
  );
};

// Normalizes register sessions when an existing structure is loaded for edit.
// Read responses now carry register_session, so this mostly passes values through
// (deriveRegisterSessions re-pins am/pm to the two earliest flagged periods). It
// also backfills structures saved before register_session was persisted: a
// Registration-type period is always a register slot, so flag any that arrive
// untagged. Periods that already carry a session (incl. lesson registrations) win.
export const seedRegisterSessions = <T extends RegisterablePeriod & { type: PeriodTypeEnum }>(
  periods: T[],
): T[] =>
  deriveRegisterSessions(
    periods.map((p) =>
      p.register_session == null && p.type === PeriodTypeEnum.Registration
        ? { ...p, register_session: RegisterSessionEnum.Am }
        : p,
    ),
  );

export type RegistrationShape = 'registration' | 'lesson_registration';

export type RegistrationGuidance =
  | { kind: 'empty' }
  | { kind: 'consistent'; am: RegistrationShape; pm?: RegistrationShape }
  | { kind: 'conflict' };

const shapeFor = (responsible: ResponsibleTypeEnum): RegistrationShape =>
  responsible === ResponsibleTypeEnum.SubjectTeacher ? 'lesson_registration' : 'registration';

// Reads the attendance_frequency configured on each selected age group. Age
// groups without a frequency (unconfigured) are skipped, so guidance reflects
// only the configured selection.
export const resolveSelectedFrequencies = (
  ageGroups: AgeGroup[],
  selectedIds: string[],
): AttendanceFrequencyResponse[] => {
  const ageGroupById = new Map(ageGroups.map((ageGroup) => [ageGroup.id, ageGroup]));
  return selectedIds
    .map((id) => ageGroupById.get(id)?.attendance_frequency)
    .filter((frequency): frequency is AttendanceFrequencyResponse => frequency != null);
};

export const deriveRegistrationGuidance = (
  frequencies: AttendanceFrequencyResponse[],
): RegistrationGuidance => {
  if (frequencies.length === 0) return { kind: 'empty' };

  const amResponsibles = new Set(frequencies.map((f) => f.am_responsible));
  if (amResponsibles.size > 1) return { kind: 'conflict' };

  const pmResponsibles = new Set(
    frequencies
      .filter((f) => f.frequency === FrequencyTypeEnum.AmPm)
      .map((f) => f.pm_responsible)
      .filter((r): r is ResponsibleTypeEnum => r != null),
  );
  if (pmResponsibles.size > 1) return { kind: 'conflict' };

  const am = shapeFor([...amResponsibles][0]);
  if (pmResponsibles.size === 1) {
    return { kind: 'consistent', am, pm: shapeFor([...pmResponsibles][0]) };
  }
  return { kind: 'consistent', am };
};

const shapeForPeriodType = (type: PeriodTypeEnum): RegistrationShape =>
  type === PeriodTypeEnum.Registration ? 'registration' : 'lesson_registration';

// The structure's register slots must exactly match the union of the selected
// age groups' attendance configuration (`guidance`). Any deviation — missing
// slot, extra/wrong amount, wrong shape, or wrong order — requires confirmation,
// as does a conflict (no single structure can fit all selected groups). Because
// there are no PM-only groups, a required PM always comes with a required AM and
// a lone structure slot is always the AM one, so no PM-only branch is needed.
export const isRegistrationConfirmationRequired = (
  guidance: RegistrationGuidance,
  periods: (RegisterablePeriod & { type: PeriodTypeEnum })[],
): boolean => {
  if (guidance.kind === 'empty') return false;
  if (guidance.kind === 'conflict') return true;

  const required = new Map<RegisterSessionEnum, RegistrationShape>([
    [RegisterSessionEnum.Am, guidance.am],
  ]);
  if (guidance.pm) required.set(RegisterSessionEnum.Pm, guidance.pm);

  const actual = new Map<RegisterSessionEnum, RegistrationShape>();
  for (const period of deriveRegisterSessions(periods)) {
    if (period.register_session != null) {
      actual.set(period.register_session, shapeForPeriodType(period.type));
    }
  }

  if (actual.size !== required.size) return true;
  for (const [session, shape] of required) {
    if (actual.get(session) !== shape) return true;
  }
  return false;
};
